import {
  FiltersRequest,
  FiltersRequestSchema,
  ListResponse,
} from "@gen/dap/v1/dap_pb";
import { useEffect, useState } from "react";
import { SortingState, Updater } from "@tanstack/react-table";
import { getDAPS } from "@services/dap";
import { getCaisseIds } from "@services/caisse";
import { useTranslation } from "react-i18next";
import { DAPImport } from "@pages/admin/daps/DAPImport";
import { useNavigate } from "react-router";
import { fields } from "./Fields";
import { TableView } from "@components/common/TableView";
import { toSort } from "@services/common";
import { useAdminStore } from "@stores/useAdminStore";
import { useUserStore } from "@stores/userStore";
import { Role } from "@gen/user/v1/user_pb";
import { create } from "@bufbuild/protobuf";

export function DAPManagement() {
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [daps, setDaps] = useState<ListResponse>();
  const [isLoadingDaps, setIsLoadingDaps] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user } = useUserStore();

  const { dapPagination, setDapPagination, dapSorting, setDapSorting } =
    useAdminStore();
  const { pageSize, pageIndex } = dapPagination;

  const [dapFilters, setDapFilters] = useState<FiltersRequest>(
    create(FiltersRequestSchema),
  );
  const [caisseOptions, setCaisseOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchDaps = async () => {
    setIsLoadingDaps(true);
    try {
      setDaps(
        await getDAPS(pageIndex + 1, pageSize, toSort(dapSorting), {
          ...dapFilters,
          caisseId: dapFilters.caisseId || undefined,
        }),
      );
    } catch (error) {
      console.error("Error fetching DAPs:", error);
    } finally {
      setIsLoadingDaps(false);
    }
  };

  const handleSorting = (updater: Updater<SortingState>) => {
    setDapSorting(updater);
    setDapPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchDaps();
  }, [dapPagination, dapSorting, dapFilters, isImportOpen]);

  useEffect(() => {
    getCaisseIds()
      .then((res) =>
        setCaisseOptions([
          { label: t("all"), value: 0 },
          ...res.identities.map((c) => ({
            label: `${c.id} - ${c.name}`,
            value: c.id,
          })),
        ]),
      )
      .catch((error) => console.error("Error fetching caisses:", error));
  }, [t]);

  return (
    <>
      <TableView
        isLoadingItems={isLoadingDaps}
        key={refreshKey}
        fields={fields(t, caisseOptions)}
        items={daps?.daps}
        title={t("admin.dap.dapManagement")}
        pagination={dapPagination}
        sorting={dapSorting}
        filters={dapFilters}
        totalItems={Number(daps?.page?.totalElements)}
        setPagination={setDapPagination}
        setSorting={handleSorting}
        setFilters={setDapFilters}
        refresh={() => fetchDaps()}
        topActions={
          user && [Role.ADMIN, Role.TRAVEL_AGENT].includes(user.role)
            ? [
                {
                  label: t("import"),
                  icon: "align_arrow_up_line",
                  do: () => setIsImportOpen(true),
                },
              ]
            : []
        }
        rowActions={(row) => [
          {
            label: t("details"),
            icon: "eye_line",
            do: () => navigate(`/admin/daps/${row.number}`),
          },
          ...(user && [Role.ADMIN, Role.TRAVEL_AGENT].includes(user.role)
            ? [
                {
                  label: t("edit"),
                  icon: "pencil_line",
                  do: () => navigate(`/admin/daps/${row.number}?edit`),
                },
              ]
            : []),
        ]}
      />
      <DAPImport
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImport={() => fetchDaps()}
      />
    </>
  );
}
